import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { adminApi } from '../../api';
import type { EmailLogsResult, EmailLog, EmailStats } from '../../api/admin';
import { isAdmin } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { useDocumentTitle, getStoredRowsPerPage, setStoredRowsPerPage } from '../../hooks/core';
import { AdminBreadcrumbs, EmailStatusChip as StatusChip, EmailStatsCards } from '../../components/admin';
import { formatDateTime } from '../../utils/dateFormatting';

function formatTemplateType(type: string): string {
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
}

const TEMPLATE_TYPES = [
  'welcome',
  'forgot_password',
  'board_invite',
  'board_completed',
  'square_request_approved',
  'hit_winner',
  'user_joined_board',
  'referral_reward',
  'low_credits',
  'weekly_report',
  'purchase_confirmation',
  'credits_added',
];

export function EmailLogsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser } = useAuth();

  useDocumentTitle('Email Logs');

  const [result, setResult] = useState<EmailLogsResult | null>(null);
  const [stats, setStats] = useState<EmailStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [recipientFilter, setRecipientFilter] = useState<string>(
    searchParams.get('recipient') || ''
  );
  const [templateFilter, setTemplateFilter] = useState<string>(
    searchParams.get('templateType') || ''
  );
  const [statusFilter, setStatusFilter] = useState<string>(
    searchParams.get('status') || ''
  );
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(() => getStoredRowsPerPage());

  // Detail dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<EmailLog | null>(null);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const [logsData, statsData] = await Promise.all([
        adminApi.getEmailLogs({
          recipient: recipientFilter || undefined,
          templateType: templateFilter || undefined,
          status: statusFilter || undefined,
          page: page + 1,
          pageSize,
        }),
        adminApi.getEmailStats(),
      ]);
      setResult(logsData);
      setStats(statsData);
    } catch (err) {
      setError('Failed to load email logs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [recipientFilter, templateFilter, statusFilter, page, pageSize]);

  useEffect(() => {
    if (!isAdmin(currentUser)) {
      navigate('/');
      return;
    }
    fetchLogs();
  }, [currentUser, navigate, fetchLogs]);

  const updateSearchParam = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params);
  };

  const handleRecipientChange = (value: string) => {
    setRecipientFilter(value);
    setPage(0);
    updateSearchParam('recipient', value);
  };

  const handleTemplateChange = (value: string) => {
    setTemplateFilter(value);
    setPage(0);
    updateSearchParam('templateType', value);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setPage(0);
    updateSearchParam('status', value);
  };

  const handleViewDetails = (log: EmailLog) => {
    setSelectedLog(log);
    setDetailDialogOpen(true);
  };

  if (loading && !result) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <AdminBreadcrumbs items={[{ label: 'Email' }, { label: 'Logs' }]} />
      <Typography variant="h4" sx={{ mb: 3 }}>
        Email Logs
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats */}
      {stats && <EmailStatsCards stats={stats} />}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Recipient Email"
              size="small"
              value={recipientFilter}
              onChange={(e) => handleRecipientChange(e.target.value)}
              sx={{ minWidth: 200 }}
              placeholder="Search by email..."
            />
            <FormControl sx={{ minWidth: 180 }} size="small">
              <InputLabel>Template Type</InputLabel>
              <Select
                value={templateFilter}
                label="Template Type"
                onChange={(e) => handleTemplateChange(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {TEMPLATE_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {formatTemplateType(type)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 150 }} size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="delivered">Delivered</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="bounced">Bounced</MenuItem>
                <MenuItem value="complained">Complained</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Recipient</TableCell>
                <TableCell>Template</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result?.logs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDateTime(log.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {log.recipientEmail}
                    </Typography>
                    {log.recipientName && (
                      <Typography variant="caption" color="text.secondary">
                        {log.recipientName}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={formatTemplateType(log.templateType)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 250,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {log.subject}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <StatusChip status={log.status} />
                    {log.errorMessage && (
                      <Tooltip title={log.errorMessage}>
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ display: 'block', mt: 0.5, cursor: 'help' }}
                        >
                          Error
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(log)}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {result?.logs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No email logs found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {result && (
          <TablePagination
            component="div"
            count={result.total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              const newSize = parseInt(e.target.value, 10);
              setPageSize(newSize);
              setStoredRowsPerPage(newSize);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        )}
      </Card>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Email Details</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Recipient
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedLog.recipientEmail}
                    {selectedLog.recipientName && ` (${selectedLog.recipientName})`}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <StatusChip status={selectedLog.status} />
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Template
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {formatTemplateType(selectedLog.templateType)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Provider
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedLog.provider}
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary">
                    Subject
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedLog.subject}
                  </Typography>
                </Grid>
                {selectedLog.messageId && (
                  <Grid size={12}>
                    <Typography variant="caption" color="text.secondary">
                      Message ID
                    </Typography>
                    <Typography variant="body2" fontFamily="monospace" sx={{ mb: 2 }}>
                      {selectedLog.messageId}
                    </Typography>
                  </Grid>
                )}
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="caption" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {formatDateTime(selectedLog.createdAt)}
                  </Typography>
                </Grid>
                {selectedLog.sentAt && (
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="caption" color="text.secondary">
                      Sent
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {formatDateTime(selectedLog.sentAt)}
                    </Typography>
                  </Grid>
                )}
                {selectedLog.deliveredAt && (
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="caption" color="text.secondary">
                      Delivered
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {formatDateTime(selectedLog.deliveredAt)}
                    </Typography>
                  </Grid>
                )}
                {selectedLog.errorMessage && (
                  <Grid size={12}>
                    <Alert severity="error">
                      <Typography variant="body2">
                        {selectedLog.errorMessage}
                      </Typography>
                    </Alert>
                  </Grid>
                )}
                {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                  <Grid size={12}>
                    <Typography variant="caption" color="text.secondary">
                      Metadata
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
                      <pre style={{ margin: 0, overflow: 'auto', fontSize: '0.75rem' }}>
                        {JSON.stringify(selectedLog.metadata, null, 2)}
                      </pre>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
